import pubsub from '@/lib/pubsub'
import { info } from '@/lib/logger'

class GTM {
  // Have we set the users ID in the dataLayer?
  private userIdSet = false
  // Have we set the users email in the dataLayer?
  private userEmailSet = false
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor() {}

  initialize() {
    pubsub.subscribe('logoutRequestCompleted', this.onLogout.bind(this))
    return this.loadGTMSnippet()
  }

  onLogout() {
    this.userEmailSet = false
    this.userIdSet = false
  }

  /**
   * Log a GTM event
   */
  logEvent(
    /** The data being sent */
    data: object
  ) {
    window.dataLayer = window.dataLayer ?? []

    info('GTM Marketing', 'logEvent:', data)

    window.dataLayer.push(data)
  }

  async loadGTMSnippet() {
    await this.setOnxUserId()
    await this.setUserEmail()

    // don't load GTM on local dev server or during e2e tests
    if (window.location.hostname === 'localhost') {
      return
    }

    /* eslint-disable-next-line */
    ;(function (
      w: Window,
      d: Document,
      s: 'script',
      l: 'dataLayer',
      i: string
    ) {
      w[l] = w[l] || []
      ;(w[l] as Array<object>).push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      })
      const f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l !== 'dataLayer' ? '&l=' + l : ''
      j.async = true
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
      f.parentNode?.insertBefore(j, f)
    })(window, document, 'script', 'dataLayer', 'GTM-NG54C7V')
  }

  /**
   * Set the onX User ID in the datalayer
   * @return Promise that resolves after the ID has been set
   */
  async setOnxUserId() {
    if (!this.userIdSet) {
      this.userIdSet = true
      const { hasValidUserSession, getUserId } =
        await import('@/services/identity-client')
      const [validSession, userId] = await Promise.all([
        hasValidUserSession(),
        getUserId(),
      ])

      if (validSession && userId) {
        // send the onx user ID to GTM for analytics tools to link users
        this.logEvent({
          event: 'onxUserIDSet',
          user_id: userId,
        })
        return
      }
      // Failed to set in dataLayer
      this.userIdSet = false
    }
  }

  /**
   * Set the onX User's hashed email in the datalayer
   * @return Promise that resolves after the email is set
   */
  async setUserEmail() {
    if (!this.userEmailSet) {
      this.userEmailSet = true
      const { getUserProfile } = await import('@/services/identity-client')
      const userProfile = await getUserProfile()
      if (userProfile?.email) {
        // We cannot send PII to the baseLayer unhashed
        const msgBuffer = new TextEncoder().encode(userProfile.email)
        const hashBuffer = await crypto.subtle?.digest('SHA-256', msgBuffer)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const hashedUserEmail = hashArray
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('')

        this.logEvent({
          event: 'userEmailSet',
          user_email: hashedUserEmail,
        })
        return
      }
      // Failed to set in dataLayer
      this.userEmailSet = false
    }
  }

  async accountCreated(app = 'fish') {
    await Promise.all([gtm.setOnxUserId(), gtm.setUserEmail()])

    this.logEvent({
      event: `${app}_account_created`,
    })
  }
}

const gtm = new GTM()

export default gtm
